import {
    ISlideEditorProps,
    SlideEditor,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/slide-editor';
import { IReactionDisposer, reaction } from 'mobx';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { EmptyArea } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/slide-editor/empty';
import OperationRenderServiceManager from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/operation-render-service-manager';
import { CopyToImage } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/copy-to-image';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { Application, Bounds, DisplayObject, IBounds, Padding, ReadonlyBounds } from '@tencent/alloy-renderer';
import { IWrapperComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/types';
import { MasterEditor } from './slide-editor/master-editor';
import { LayoutEditor } from './slide-editor/layout-editor';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { StableLayerManager } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/stable-layer/stable-layer-manager';
import {
    IMasterLayoutEditService,
} from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import { SlideEditorCache } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/slide-editor/cache';
import debounce from 'lodash/debounce';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import {
    IEditorLayerServiceProps, IPcEditorAreaService,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/pc-content-service.interface';
import {
    IContentSize,
    IViewportSize,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/base/types';
import { IPcScrollService } from '@src/slide/application/service/pc-scroll-service/pc-scroll-service.interface';
import { statusReport007 } from '@src/slide/infrastructure/module/dependent/report/real-time-report/real-time';
import { Reporter007, Reporter007ActionType } from '@src/slide/infrastructure/module/dependent/report/real-time-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

const RENDER_DEBOUNCE_TIME = 20;

export class PcEditorAreaService implements IPcEditorAreaService {
    public currentSlideEditor: SlideEditor | null | undefined;
    public copyToImage: CopyToImage;

    public updatePosition = debounce(() => {
        this.currentSlideEditor?.updatePosition();
    }, RENDER_DEBOUNCE_TIME);

    private props: IEditorLayerServiceProps;
    private scale: number;
    private stableLayer: StableLayerManager;
    private slideEditorMap: Map<string, SlideEditor> = new Map<string, SlideEditor>();
    private masterEditorMap: Map<string, SlideEditor> = new Map<string, SlideEditor>();
    private layoutEditorMap: Map<string, SlideEditor> = new Map<string, SlideEditor>();
    private emptyAreaInstance: EmptyArea;
    private changeListenerOff: Array<IReactionDisposer>;
    private disposeSlideTimer: NodeJS.Timeout | undefined;
    private buildSlideTimer: NodeJS.Timeout | undefined;
    private editorCache: SlideEditorCache;
    private maxEditorNumber: number;
    private hasReported = false;

    public initService(props: IEditorLayerServiceProps): void {
        const {
            scale = 1,
        } = props;
        this.props = props;
        this.scale = scale;
        this.calcMaxEditorNumber();
        this.editorCache = new SlideEditorCache(20, this.handleEditorCacheDelete, this.getReserveSlideIndexRange);
        // 提供复制元素生成图片能力
        this.copyToImage = new CopyToImage(
            scale,
            this.forceBuildSlideSync.bind(this),
        );
        this.initStableLayer();
        this.buildFirstSlideCache();
        // 将当前页置为可见
        this.updateCurrentSlide();
        // 监听slides数据变更以及尺寸变更
        this.addSlidesChangeListener();
    }

    // 外层react component更新时调用，以处理画布尺寸变更
    public updateWithData(scale: number, contentSize: IContentSize, viewportSize: IViewportSize): void {
        this.scale = scale;
        this.props.scale = scale;
        this.props.viewportSize = viewportSize;
        this.props.contentSize = contentSize;
        const { currentSlide } = this.props.services.selectionService;
        const editorMap = this.getEditorMapFromModel(currentSlide as ISlideModel);
        this.stableLayer.updateTranslate();
        if (!currentSlide || editorMap.get(currentSlide.key)) {
            // 已经有缓存的slide走同步的切页
            this.updateManagersData();
        } else {
            if (currentSlide !== this.currentSlideEditor?.slide) {
                // 切页时同步隐藏上一次的页面
                this.currentSlideEditor?.hidden();
            }
            this.asyncUpdateData();
        }
    }

    public getCurrentSlideBounds(
        slideModel: ISlideModel,
        pcScrollService?: IPcScrollService,
    ): ReadonlyBounds | undefined {
        // 对齐历史逻辑，切页时会 resize 两次，第一次 resize 的 slideLeft 和 slideTop 是错的，使用 worldBounds 计算 maxSize 会得到错误的结果，
        // 直接返回后会使用 uiModel 中的数据计算 maxSize
        if (!this.getComponentByModel(slideModel.children?.[0])?.worldBounds) return;
        // 使用 slideComponent 的 worldBounds，不需要计算的元素可以放在 slideComponent 同层级
        const slideEditorBounds = this.getSlideEditorByKey(slideModel.key)
            ?.slideComponent?.worldBounds;
        if (!slideEditorBounds) return;
        const bounds = new Bounds();
        bounds.addBounds(slideEditorBounds as IBounds);
        const regionElements = OperationRenderServiceManager.getInstance().getAllComponents();
        if (!(regionElements.length > 0)) {
            return bounds;
        }
        if (!pcScrollService) return;
        const { currentScrollX, currentScrollY, currentTranslateX, currentTranslateY } = pcScrollService;
        const pad = new Padding();
        const elementBounds = new Bounds();
        regionElements.forEach((element) => {
            // 大画布缩小后，存在元素画布和操作层画布大小和位置不同的情况，需要将操作层元素 bounds 的原点调整到和元素层相同的位置
            elementBounds.clear();
            elementBounds.addBounds(element.worldBounds);
            pad.clear();
            const top = currentScrollY - currentTranslateY;
            const left = currentScrollX + currentTranslateX;
            pad.set(top, -left, -top, left);
            elementBounds.pad(pad);
            bounds.addBounds(elementBounds);
        });

        return bounds;
    }

    // 外层react component协作时调用
    public dispose(): void {
        this.slideEditorMap.forEach((slideEditor: SlideEditor, slideKey) => {
            slideEditor?.dispose();
            this.stableLayer.cleanCache(slideEditor.slide);
            this.slideEditorMap.delete(slideKey);
            this.editorCache.deleteCache(slideEditor);
        });
        this.currentSlideEditor = null;
        this.props.services.animManager.editSlideComponent = undefined;
        this.changeListenerOff?.forEach(reaction => reaction?.());
        this.removeMasterLayoutCache();
        this.editorCache.dispose();

        if (this.disposeSlideTimer) {
            clearTimeout(this.disposeSlideTimer);
        }
        if (this.buildSlideTimer) {
            clearTimeout(this.buildSlideTimer);
        }
    }

    public getSlideEditorByKey(key: string): SlideEditor | undefined {
        return this.slideEditorMap.get(key);
    }

    public getLayoutEditorByKey(key: string): SlideEditor | undefined {
        return this.layoutEditorMap.get(key);
    }

    // 通过model查找对应的canvas component
    public getComponentByModel(model: IBaseModel): DisplayObject | undefined {
        const slideElements = this.currentSlideEditor?.slideComponent?.elementsLayer?.children || [];
        const component = slideElements.find((child: IWrapperComponent) => child?.factoryOptions?.model === model);
        if (component) {
            return component;
        }
        const regionElements = OperationRenderServiceManager.getInstance().getAllComponents();
        return regionElements.find((child: IWrapperComponent) => child?.factoryOptions?.model === model);
    }


    // 强制指定的slide立即初始化
    public forceBuildSlideSync(slide: ISlideModel, customOptions?: Partial<ISlideEditorProps>): SlideEditor {
        // 触发组件树构建
        let slideEditor = this.slideEditorMap.get(slide.key);
        if (!slideEditor) {
            TaskManager.cancelTaskByKey(`render-${slide.key}`);
            slideEditor = this.buildSingleSlideCache(slide, customOptions);
        }
        // 将组件树挂载到画布上
        slideEditor.mountSlide();
        return slideEditor;
    }

    // 更新指定slide的尺寸
    private updateModelInfo(model: ISlideModel): void {
        const editor = this.getEditorMapFromModel(model).get(model?.key);
        if (editor) {
            editor.updateInfo(this.scale, this.props.contentSize, this.props.viewportSize);
        }
    }

    private handleEditorCacheDelete = (slideEditor: SlideEditor) => {
        slideEditor?.dispose?.();
        this.stableLayer?.cleanCache(slideEditor?.slide);
        this.slideEditorMap.delete(slideEditor?.slide?.key);
    };

    private asyncUpdateData() {
        const { currentSlide, currentSlideIndex, slides } = this.props.services.selectionService;

        // 没有缓存的slide走异步的切页
        if (!TaskManager.getTaskWithKey(`updateCurrent-${(currentSlide as ISlideModel).key}`)) {
            TaskManager.addTaskWithBlock(`updateCurrent-${(currentSlide as ISlideModel).key}`, TaskPriority.HIGH + 2, () => {
                this.updateManagersData();
            });
        }
        // 快速切页时cancel掉上一个任务
        const preSlide = slides[currentSlideIndex - 1];
        const nextSlide = slides[currentSlideIndex + 1];
        if (TaskManager.getTaskWithKey(`updateCurrent-${preSlide?.key}`)) {
            TaskManager.cancelTaskByKey(`updateCurrent-${preSlide?.key}`);
        }
        if (TaskManager.getTaskWithKey(`updateCurrent-${nextSlide?.key}`)) {
            TaskManager.cancelTaskByKey(`updateCurrent-${nextSlide?.key}`);
        }
    }

    // 显示或隐藏无slide时的托底页
    private renderEmpty() {
        if (!this.emptyAreaInstance) {
            this.emptyAreaInstance = new EmptyArea(this.props.canvasWrapper);
        }
        this.emptyAreaInstance.update();
    }

    // 创建出第一页的渲染树，其他页的渲染树构建在visibleSlideKeyList变化的回调中处理
    private buildFirstSlideCache() {
        const [slide] = this.props.services.uiDataService.slideModelList;
        if (!slide) {
            return;
        }
        this.buildSingleSlideCache(slide);
    }

    private buildSingleSlideCache(model: ISlideModel, customOptions?: Partial<ISlideEditorProps>): SlideEditor {
        const consName = this.getEditorFromModel(model);
        const slideEditor = new consName({
            slide: model,
            scale: this.scale,
            stableLayer: this.stableLayer,
            viewportSize: this.props.viewportSize,
            contentSize: this.props.contentSize,
            canvasWrapper: this.props.canvasWrapper,
            services: this.props.services,
            onSlideWillRender: this.props.onSlideWillRender,
            onSlideDidRender: this.props.onSlideDidRender,
            ...customOptions,
        });
        const editorMap = this.getEditorMapFromModel(model);
        editorMap.set(model.key, slideEditor);
        this.props.canvasWrapper.appendChild(slideEditor.canvasDom);
        if (this.editorCache.shouldCacheEditor(slideEditor)) {
            this.editorCache.cacheEditor(slideEditor);
        }
        return slideEditor;
    }

    /**
     * 获取需要保留渲染树的slide范围
     */
    private getReserveSlideIndexRange = (): string[] => {
        // 需要同时考虑方向键翻页与鼠标滚动的场景
        // 方向键翻页时的策略是，以当前选中的页面为中心，上下扩展窗口大小的一半+2个slide
        // 鼠标滚动时的策略是，将visibleSlide上下扩展2个slide
        // 取二者的并集
        const { slideListRenderState } = this.props.services;
        let visibleSlideKeyList: Array<string> = [];
        if (slideListRenderState) {
            visibleSlideKeyList = slideListRenderState.getVisibleSlideKeyList();
        }
        const slideList = this.props.services.uiDataService.slideModelList;
        // 方向键翻页的slide
        const { currentSlideIndex } = this.props.services.selectionService;
        const halfVisibleSize = Math.ceil(visibleSlideKeyList.length / 2);
        const slideKeyList: string[] = [];
        if (currentSlideIndex > -1) {
            slideList.forEach((slide) => {
                const index = this.props.services.uiDataService.presentation.children.indexOf(slide) ?? -1;
                if (index > -1 && index >= currentSlideIndex - halfVisibleSize - 2
                && index <= currentSlideIndex + halfVisibleSize + 2) {
                    slideKeyList.push(slide.key);
                }
            });
        }
        let indexList;
        if (visibleSlideKeyList.length) {
        // 鼠标滚动的slide
            indexList = visibleSlideKeyList.map((key) => {
                const index = slideList.find(slide => slide.key === key)?.listIndex || 0;
                return { index, key };
            });
            indexList = indexList.sort((a, b) => a.index - b.index);
            const minIndex = (slideList.find(item => item.key === indexList[0].key)?.listIndex || 0) - 2;
            const maxIndex = (
                slideList.find(item => item.key === indexList[indexList.length - 1].key)?.listIndex || 0
            ) + 2;
            slideList.forEach((slide) => {
                if ((slide.listIndex || slide.listIndex === 0)
            && slide.listIndex >= minIndex
            && slide.listIndex <= maxIndex
            && slideKeyList.indexOf(slide.key) === -1) {
                    slideKeyList.push(slide.key);
                }
            });
        }
        if (this.maxEditorNumber && slideKeyList.length > this.maxEditorNumber && !this.hasReported) {
            statusReport007(Reporter007ActionType.VISABLE_SLIDE_KEY_LIST_OVER_LIMIT, Reporter007.ERROR);
            const endTs = Date.now();
            const startTs = endTs - (24 * 60 * 60 * 1000);
            window.log.report('USER_REPORT', {
                startTs,
                endTs,
            }).then((reportId) => {
                tdw({
                    opername: TDWOperationType.PPT_QUALITY,
                    module: TDWModuleType.PC_EDIT,
                    action: TDWActionType.RENDER_TREE_LIMIT,
                    ver6: JSON.stringify({
                        type: Reporter007ActionType.VISABLE_SLIDE_KEY_LIST_OVER_LIMIT,
                        maxEditorNumber: this.maxEditorNumber,
                        visibleSlideKeyListLength: visibleSlideKeyList.length,
                        slideKeyList: slideKeyList.length,
                        currentSlideIndex,
                        indexList,
                        cellHeight: this.props.services.listService?.getCellHeight(),
                        listHeight: this.props.services.listService?.listClientHeight,
                        reportId,
                    }),
                });
            });
            this.hasReported = true;
        }
        return slideKeyList;
    };

    private handleVisibleSlideChange(): void {
        this.calcMaxEditorNumber();
        const renderTasks = TaskManager.getAllTasksContainsKey('render-');
        const disposeTasks = TaskManager.getAllTasksContainsKey('dispose-');
        // 没有渲染任务与销毁任务时认为当前状态已经稳定下来了，去检查当前的map中有多少个树
        if (!renderTasks.length
            && !this.hasReported
            && !disposeTasks.length
            && this.maxEditorNumber
            && this.slideEditorMap.size > this.maxEditorNumber) {
            statusReport007(Reporter007ActionType.RIGHT_RENDER_TREE_OVER_LIMIT, Reporter007.ERROR);
            const endTs = Date.now();
            const startTs = endTs - (24 * 60 * 60 * 1000);
            window.log.report('USER_REPORT', {
                startTs,
                endTs,
            }).then((reportId) => {
                tdw({
                    opername: TDWOperationType.PPT_QUALITY,
                    module: TDWModuleType.PC_EDIT,
                    action: TDWActionType.RENDER_TREE_LIMIT,
                    ver6: JSON.stringify({
                        type: Reporter007ActionType.VISABLE_SLIDE_KEY_LIST_OVER_LIMIT,
                        maxEditorNumber: this.maxEditorNumber,
                        slideEditorMapSize: this.slideEditorMap.size,
                        cellHeight: this.props.services.listService?.getCellHeight(),
                        listHeight: this.props.services.listService?.listClientHeight,
                        reportId,
                    }),
                });
            });
            this.hasReported = true;
        }
        if (this.disposeSlideTimer) {
            clearTimeout(this.disposeSlideTimer);
        }
        if (this.buildSlideTimer) {
            clearTimeout(this.buildSlideTimer);
        }
        // 可视区域外的slide在visibleSlideKeyList停止变化0.5s后dispose
        this.disposeSlideTimer = setTimeout(() => {
            this.handleSlideDispose();
        }, 500);
        // 可视区域内的slide在visibleSlideKeyList停止变化0.2s后开始构建
        this.buildSlideTimer = setTimeout(() => {
            this.handleSlideBuild();
        }, 200);
    }

    private handleSlideBuild(): void {
        const slideKeyList = this.getReserveSlideIndexRange();
        this.props.services.uiDataService.slideModelList.forEach((model, index) => {
            // 可视区域内的slide
            if (slideKeyList.indexOf(model.key) > -1 && !model.preload) {
                // 缓存中没有该slide的渲染树
                if (!this.slideEditorMap.get(model.key)) {
                    if (index === 0) {
                        this.buildSingleSlideCache(model);
                    }
                    // 将构建该slide的渲染树加入任务队列
                    if (!TaskManager.getTaskWithKey(`render-${model.key}`)) {
                        TaskManager.addTaskWithBlock(`render-${model.key}`, TaskPriority.NORMAL, () => {
                            if (!this.slideEditorMap.get(model.key)) {
                                this.buildSingleSlideCache(model);
                            }
                        });
                    }
                } else {
                    // 缓存中有该slide的渲染树,cancel掉该slide的dispose任务
                    const disposeTask = TaskManager.getTaskWithKey(`dispose-${model.key}`);
                    disposeTask?.cancel();
                    // dispose任务在执行的过程中无法取消，只能在执行结束后再添加一条render任务
                    if (disposeTask?.isExecuting) {
                        TaskManager.addTaskWithBlock(`render-${model.key}`, TaskPriority.LOW, () => {
                            if (!this.slideEditorMap.get(model.key)) {
                                this.buildSingleSlideCache(model);
                            }
                        });
                    }
                }
            }
        });
    }

    private updateManagersData() {
        this.stableLayer?.updateWithData(this.scale);
        this.updateCurrentSlide();
    }

    private handleSlideDispose(): void {
        const slideKeyList = this.getReserveSlideIndexRange();
        this.props.services.uiDataService.slideModelList.forEach((slide) => {
            if (slideKeyList.indexOf(slide.key) === -1) {
                const slideEditor = this.slideEditorMap.get(slide.key);
                // 缓存中有该slide的渲染树
                if (slideEditor) {
                    // 没有在 editorCache 中缓存，则直接销毁，否则先不销毁
                    if (!this.editorCache.hasCache(slideEditor)) {
                        slideEditor?.dispose?.();
                        this.stableLayer?.cleanCache(slideEditor?.slide);
                        this.slideEditorMap.delete(slide.key);
                    }
                } else {
                    // 缓存中没有该slide的渲染树,cancel掉该slide的render任务
                    const renderTask = TaskManager.getTaskWithKey(`render-${slide.key}`);
                    renderTask?.cancel();
                    // render任务若正在执行中则无需额外添加一条dispose任务，最多冗余一个渲染树问题不大
                }
            }
        });
    }

    private addSlidesChangeListener() {
        this.changeListenerOff = [
            reaction(() => [
                this.props.services.uiDataService.presentation.props.slideSizeType,
                this.props.services.uiDataService.presentation.props.slideWidth,
                this.props.services.uiDataService.presentation.props.slideHeight,
            ], () => {
                this.handleListChange(this.props.services.uiDataService.slideModelList, this.slideEditorMap);
            }),
            reaction(
                () => [
                    this.props.services.slideListRenderState?.getVisibleSlideKeyList().slice(),
                    this.props.services.selectionService.currentSlideIndex,
                ],
                () => {
                    this.handleVisibleSlideChange();
                },
            ),
            reaction(() => [
                this.getMasterLayoutEditService()?.editType,
            ], ([editType]) => {
                if (editType === EDIT_TYPE.MASTER_LAYOUT) return;
                this.removeMasterLayoutCache();
            }),
            reaction(() => this.props.services.uiDataService.slideModelList, (slideList) => {
                this.removeEditorByModelList(slideList, this.slideEditorMap);
            }),
        ];

        // master 和 layout 的转换，等到所有 slide 都渲染完成后再进行
        // 只做移除节点操作，创建节点操作在用户切换的时候同步进行。母版、版式过多时压力太大。
        eventBus.on(EventKeys.ALL_SLIDE_DATA_CONVERT, () => {
            this.changeListenerOff.push(
                reaction(
                    () => this.props.services.uiDataService.masterModelList,
                    masterList => this.removeEditorByModelList(masterList, this.masterEditorMap),
                ),
                reaction(
                    () => this.props.services.uiDataService.layoutModelList,
                    layoutList => this.removeEditorByModelList(layoutList, this.layoutEditorMap),
                ),
            );
        });
    }

    private removeMasterLayoutCache() {
        this.masterEditorMap.forEach((editor, key) => {
            // 触发所有子孙节点dispose生命周期
            editor?.dispose?.();
            this.masterEditorMap.delete(key);
            this.stableLayer?.cleanCache(editor?.slide);
            this.editorCache.deleteCache(editor);
        });
        this.layoutEditorMap.forEach((editor, key) => {
            // 触发所有子孙节点dispose生命周期
            editor?.dispose?.();
            this.layoutEditorMap.delete(key);
            this.stableLayer?.cleanCache(editor?.slide);
            this.editorCache.deleteCache(editor);
        });
    }

    private removeEditorByModelList(modelList: ISlideModel[], editorMap: Map<string, SlideEditor>) {
        const newMap = new Map<string, boolean>();
        modelList.forEach((model: ISlideModel) => {
            newMap.set(model.key, true);
        });
        editorMap.forEach((_, modelKey) => {
            if (!newMap.has(modelKey)) {
                const editor = editorMap.get(modelKey);
                // 触发所有子孙节点dispose生命周期
                editor?.dispose?.();
                editorMap.delete(modelKey);
                this.stableLayer?.cleanCache(editor?.slide);
                this.editorCache.deleteCache(editor as SlideEditor);
            }
        });
    }

    /**
     * slideModelList、masterModelList、layoutModelList 变化时的处理
     * @param modelList
     * @param editorMap
     */
    private handleListChange(modelList: ISlideModel[], editorMap: Map<string, SlideEditor>) {
        this.removeEditorByModelList(modelList, editorMap);
        for (let i = 0; i < modelList.length; i++) {
            const model = modelList[i];
            if (!editorMap.get(model.key)) {
                if (i === 0) {
                    this.buildSingleSlideCache(model);
                }
                TaskManager.addTaskWithBlock(`render-${model.key}`, TaskPriority.LOW, () => {
                    if (!editorMap.get(model.key)) {
                        this.buildSingleSlideCache(model);
                    }
                });
            }
        }
    }

    /**
     * 当前页面切换，需要将新的页面挂载到屏上
     * @private
     */
    private updateCurrentSlide() {
        const { currentSlide } = this.props.services.selectionService;
        const renderServiceManager = OperationRenderServiceManager.getInstance();
        let lastCurrentEditor: SlideEditor | MasterEditor | LayoutEditor | null = null;
        const editorMap = this.getEditorMapFromModel(currentSlide as ISlideModel);
        // 更新当前页
        if (currentSlide !== this.currentSlideEditor?.slide) {
            if (!currentSlide) {
                // 没有页面的时候会触发到这里，销毁掉当前页相关的。
                if (this.currentSlideEditor) {
                    this.currentSlideEditor.canvasDom.style.display = 'none';
                    this.currentSlideEditor = null;
                    this.props.services.animManager.editSlideComponent = undefined;
                    renderServiceManager.currentCanvasElementLayer = undefined;
                }
                // 显示或隐藏无slide时的托底页
                this.renderEmpty();
                return;
            }
            let newCurrentSlideEditor;
            // 拿到新的slideEditor
            if (!editorMap.has(currentSlide.key)) {
                newCurrentSlideEditor = this.buildSingleSlideCache(currentSlide);
            } else {
                newCurrentSlideEditor = editorMap.get(currentSlide.key);
            }
            if (this.currentSlideEditor) {
                lastCurrentEditor = this.currentSlideEditor;
            }
            this.currentSlideEditor = newCurrentSlideEditor;
        }
        // 调整当前页的尺寸
        this.updateModelInfo(currentSlide as ISlideModel);
        // 当前页显示，如果异步创建过就直接挂载触发渲染，否则异步创建转同步创建子树，然后再挂载触发渲染
        this.currentSlideEditor?.display();
        // 隐藏上次的当前页
        lastCurrentEditor?.hidden();
        // 更新动画需要的组件
        this.props.services.animManager.editSlideComponent = this
            .currentSlideEditor?.slideComponent;
        renderServiceManager.currentCanvasElementLayer = this.currentSlideEditor?.slideComponent?.elementsLayer;
        this.copyToImage.updateElementLayer(this.currentSlideEditor?.slideComponent?.elementsLayer);
        this.stableLayer?.updateStableLayer(currentSlide);
        // 显示或隐藏无slide时的托底页
        this.renderEmpty();
    }

    private getApplication = (): Application => this.currentSlideEditor?.application as Application;

    private initStableLayer() {
        if (this.props.stableLayerWrapper) {
            this.stableLayer = new StableLayerManager({
                scale: this.scale,
                getApplication: this.getApplication,
                elementWrapper: this.props.stableLayerWrapper,
                updateStableLayerTranslate: this.props.updateStableLayerTranslate,
                services: this.props.services,
            });
        }
    }

    private getEditorMapFromModel(model: ISlideModel) {
        if (UIModelType.MASTER === model?.type) return this.masterEditorMap;
        if (UIModelType.LAYOUT === model?.type) return this.layoutEditorMap;
        return this.slideEditorMap;
    }

    private getEditorFromModel(model: ISlideModel) {
        if (UIModelType.MASTER === model?.type) return MasterEditor;
        if (UIModelType.LAYOUT === model?.type) return LayoutEditor;
        return SlideEditor;
    }

    private getMasterLayoutEditService(): IMasterLayoutEditService | undefined {
        return this.props.services.masterLayoutEditService;
    }

    private calcMaxEditorNumber() {
        const cellHeight = this.props.services.listService?.getCellHeight();
        const listHeight = this.props.services.listService?.listClientHeight;
        if (cellHeight && listHeight) {
            const maxCellNumInView = Math.ceil(listHeight / cellHeight);
            // 一屏内最多的cell数量+2(最上面和最下面的cell只漏了一个边的情况)
            // 再+4（上下各有两个buffer）再*2（当前选中的slide+左侧列表现有的）为最大的editor数量
            this.maxEditorNumber = (maxCellNumInView + 6) * 2;
        }
    }
}
